import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { EmployeeConflict } from '../models/employee-conflict';

export interface EmployeeWarningStatus {
  status: string;
  expirationDate?: string;
}

@Component({
  standalone: false,
  selector: 'app-recommend-employee-conflicts',
  template: `
    <div class="tooltip-container" [class.tooltip-warning]="severity === 'warning'" [class.tooltip-error]="severity === 'error'">
      <div class="tooltip-arrow"></div>
      <div class="tooltip-header" *ngIf="severity">
        <strong>{{ severity === 'error' ? 'Assignment unavailable' : 'Assignment warning' }}</strong>
        <button mat-icon-button type="button" aria-label="Close assignment warning" (click)="dismissed.emit()">
          <mat-icon svgIcon="close"></mat-icon>
        </button>
      </div>
      <div class="tooltip-content">
        <ng-container *ngIf="warningStatus?.status === 'Suspended' || warningStatus?.status === 'Retired'">
          <div class="tooltip-item">
            <div class="icon-container">
              <mat-icon svgIcon="account" class="item-icon warning-icon"></mat-icon>
            </div>
            <div class="conflict-info">
              <span class="conflict-title">The employee is {{ warningStatus.status }}</span>
              <span class="conflict-title" *ngIf="warningStatus.expirationDate">until: {{ warningStatus.expirationDate | date: 'y-MM-dd'}}</span>
            </div>
          </div>
        </ng-container>
        <ng-container *ngIf="conflicts && conflicts.length">
          <div class="tooltip-item">
            <div class="icon-container">
              <mat-icon svgIcon="ferry" class="item-icon conflict-icon"></mat-icon>
            </div>
            <div class="conflict-info">
              <span class="conflict-title">The employee is assigned to another project(s) at the same dates.</span>
              <span class="conflict-title">Hull: {{ conflictingHulls }}</span>
            </div>
          </div>
        </ng-container>

        <ng-container *ngIf="otherWarnings.length">
          <ng-container *ngFor="let warning of otherWarnings">
            <div class="tooltip-item">
              <div class="icon-container">
                <mat-icon svgIcon="account" class="item-icon warning-icon"></mat-icon>
              </div>
              <div class="conflict-info">
                <span class="conflict-title">{{ warning }}</span>
              </div>
            </div>
          </ng-container>
        </ng-container>
<!--        {{otherWarnings | json}}-->
<!--        {{conflicts | json}}-->
<!--        {{warningStatus | json}}-->
        <ng-container *ngIf="!otherWarnings.length && !conflicts.length && !warningStatus">
          <div class="tooltip-item">
            <div class="icon-container">
              <mat-icon svgIcon="account" class="item-icon warning-icon"></mat-icon>
            </div>
            <div class="conflict-info">
              <span class="conflict-title">Employee has active project but not the same date.</span>
            </div>
          </div>
        </ng-container>
      </div>
    </div>
  `,
  styles: [`
    .tooltip-container {
      position: relative;
      background-color: #F0F0F0;
      border: 1px solid #404B5B;
      border-radius: 4px;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
      min-width: 250px;
      max-width: 350px;
      overflow: hidden;
      z-index: 1000;
    }

    .tooltip-arrow {
      position: absolute;
      top: -8px;
      left: 12px;
      width: 16px;
      height: 8px;
      overflow: hidden;
    }

    .tooltip-warning { background-color: #fff8e1; border-color: #a15c00; color: #6b4500; }
    .tooltip-error { background-color: #fff0f0; border-color: #b71c1c; color: #8b1616; }
    .tooltip-header { display: flex; align-items: center; justify-content: space-between; padding-left: 12px; gap: 8px; }

    .tooltip-arrow:after {
      content: '';
      position: absolute;
      width: 12px;
      height: 12px;
      background: white;
      transform: translateY(50%) rotate(45deg);
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
    }

    .tooltip-content {
      padding: 12px;
    }

    .tooltip-item {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 10px;
    }

    .tooltip-item:last-child {
      margin-bottom: 0;
    }

    .icon-container {
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 24px;
      height: 24px;
    }

    .item-icon {
      font-size: 20px;
      height: 25px;
      width: 25px;
      line-height: 20px;
    }

    .conflict-info {
      display: flex;
      flex-direction: column;
      flex: 1;
    }

    .conflict-title {
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      word-wrap: break-word;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmployeeConflictsTooltipComponent implements OnChanges {
  @Input() conflicts: EmployeeConflict[] = [];
  @Input() warningStatus: EmployeeWarningStatus | null = null;
  @Input() otherWarnings: string[] = [];
  @Input() severity: 'warning' | 'error' | null = null;
  @Output() dismissed = new EventEmitter<void>();

  conflictingHulls: string = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['conflicts']) {
      this.updateConflictingHulls();
    }
  }

  private updateConflictingHulls(): void {
    if (this.conflicts && this.conflicts.length) {
      this.conflictingHulls = this.conflicts
        .map(conflict => conflict.hull)
        .join(', ');
    }
  }
}
